/**
 * Points that need not be whole are held to a quarter grid, so each is a
 * dyadic fraction and therefore exact in floating point. Allowing tenths
 * would make a week total like 11.600000000000001 reachable, and every
 * display site — day tile, week tile, summary rows, Forventet column — would
 * need a rounding rule to hide it.
 *
 * Week goals are held to it too, because they are summed across widgets in
 * the same way scores are: `DayStrip.weekTotals` and weekly-point-summary's
 * `sumForWidgets` both add every instance's goal together and show the
 * result.
 */
export function isQuarterPoint(value: number): boolean {
  return Number.isFinite(value) && Number.isInteger(value * 4);
}

export const QUARTER_MESSAGE = "Må være et helt, halvt eller kvart poeng.";

interface WeekGoalOptions {
  /** Omitted or non-finite when the kind's weekly score has no ceiling. */
  max?: number;
  /** recurring-tasks alone treats 0 as "no target" rather than an error. */
  allowZero?: boolean;
}

/**
 * The shared Ukesmål rules, in the order a user meets them. Returns the
 * message for the first rule broken, or undefined when the goal is fine.
 *
 * workday-log does not use this: it carries one goal per Scorable part
 * (docs/adr/0010), each with its own label, and its instance-level
 * `weekGoal` is derived from them rather than typed.
 */
export function weekGoalError(
  goal: number,
  { max, allowZero = false }: WeekGoalOptions = {},
): string | undefined {
  if (!Number.isFinite(goal)) return "Ukesmål kreves.";
  if (allowZero) {
    if (goal < 0) return "Ukesmål kan ikke være negativt.";
  } else if (goal <= 0) {
    return "Ukesmål må være over 0.";
  }
  if (!isQuarterPoint(goal)) return QUARTER_MESSAGE;
  if (max !== undefined && Number.isFinite(max) && goal > max) {
    return `Ukesmål kan ikke overstige maks (${max}p).`;
  }
  return undefined;
}
